import { formatAge, formatTimestamp } from '@/lib/format/format';
import { retryOf } from '@/features/automation/vocab';
import type { Source, SourceHealth } from './api';

// A source in the design system's words. Disabled retires it; Paused stops it until it's resumed.

export type SourceStatus = 'enabled' | 'paused' | 'disabled';

export const sourceStatus = (source: { enabled: boolean; paused: boolean }): SourceStatus =>
  !source.enabled ? 'disabled' : source.paused ? 'paused' : 'enabled';

/** Enabled and not paused: can run by hand and on its schedule. */
export const isActive = (source: { enabled: boolean; paused: boolean }): boolean =>
  source.enabled && !source.paused;

/** What holds a requested re-read back, as it follows "is read"; null when the next run reads it. */
export function reReadHold(
  source: { enabled: boolean; paused: boolean } | undefined,
  automationPaused: boolean,
): string | null {
  if (source && !source.enabled) return 'once the source is enabled';
  if (source?.paused) return 'once the source is resumed';
  if (automationPaused) return 'once automation resumes';
  return null;
}

export type HealthStatus =
  'healthy' | 'slipping' | 'failing' | 'silent' | 'capped' | 'paused' | 'overdue' | 'retrying' | 'not-run';

const HEALTH: Partial<Record<string, HealthStatus>> = {
  Healthy: 'healthy',
  Slipping: 'slipping',
  Failing: 'failing',
  Silent: 'silent',
  Capped: 'capped',
  Paused: 'paused',
  Overdue: 'overdue',
  Retrying: 'retrying',
  NotRun: 'not-run',
};

/** What clears Capped: the cap is the API's setting. */
export const CAP_REMEDY = "Raise Scraping:MaxEventsPerRun in the API's configuration and redeploy.";

// Two points draw a line, not a trend.
const TREND_MIN = 3;

/** The API's verdict; undefined from an API that doesn't judge health yet. */
export const healthOf = (source: Source): SourceHealth | undefined => source.health;

/** The verdict in the design system's words. A disabled source has none: it doesn't run. */
export const healthStatus = (source: Source): HealthStatus | undefined =>
  HEALTH[healthOf(source)?.state ?? ''];

/** `Health`'s props: the server's reason, except a pause, which says who and when, and the two scheduled states, which say when. */
export function healthProps(source: Source, now = new Date()) {
  const status = healthStatus(source);
  const health = healthOf(source);
  if (!status || !health) return undefined;
  return {
    status,
    since: healthSince(status, source, now) ?? health.reason,
    trend: health.trend.length >= TREND_MIN ? health.trend : undefined,
  };
}

function healthSince(status: HealthStatus, source: Source, now: Date): string | undefined {
  const health = healthOf(source);
  if (status === 'paused') return pausedSince(source, now);
  if (status === 'overdue' && health?.overdue)
    return `was due ${formatTimestamp(health.overdue.dueAt, now)}`;
  if (status === 'retrying' && health?.retry)
    return `${retryOf(health.retry)} due ${formatTimestamp(health.retry.dueAt, now)}`;
  return undefined;
}

/** "by ana, 2 h ago": who paused it and when. */
export function pausedSince(
  source: { pausedAt: string | null; pausedBy: string | null },
  now = new Date(),
): string {
  const age = source.pausedAt ? formatAge(source.pausedAt, now) : '';
  const when = !age ? '' : age === 'just now' ? 'just now' : `${age} ago`;
  return [source.pausedBy ? `by ${source.pausedBy}` : '', when].filter(Boolean).join(', ');
}
